import { useEffect, useState } from 'react'
import { useChannel } from '@/context/ChannelContext'
import { useShowcase } from '@/showcase/useShowcase'
import { track, trackError } from '@/lib/analytics'
import {
  bulkBackfillArchive,
  getShowcaseOverview,
  getShowcasePosts,
  PostsCountsByStatus,
  ShowcaseApiError,
  ShowcaseOverview,
  ShowcaseStatus,
  WhatToPublish,
} from '@/api/showcase'

/**
 * Настройки Витрины — §8 спеки. 4 секции сверху вниз:
 *   1. «Витрина в цифрах»
 *   2. «Какие посты публиковать» + модалка про короткие посты + кнопка бэкфилла
 *   3. «Согласие» (дата, версия)
 *   4. «Опасная зона · Выключить витрину»
 */
export function SettingsPage() {
  const { selectedChannel } = useChannel()
  const { state, actions } = useShowcase(selectedChannel?.uuid)

  if (!state) return null

  return (
    <div className="settings">
      <StatsSection state={state} channelUUID={selectedChannel?.uuid} />
      <PublishRulesSection
        state={state}
        onChange={actions.updateSettings}
        channelUUID={selectedChannel?.uuid}
      />
      <ConsentSection state={state} />
      <DangerZone state={state} onDisable={actions.disable} />

      <style>{`
        .settings {
          display: flex;
          flex-direction: column;
          gap: 18px;
        }
        .sec {
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 16px;
          padding: 22px 24px;
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
        .sec.sec-danger {
          border-color: rgba(229, 70, 70, 0.3);
          background: linear-gradient(135deg, #fff5f5, #ffffff);
        }
        .sec-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
        }
        .sec-title {
          font-size: 16px;
          font-weight: 800;
          color: var(--tg-ink, #111);
          margin: 0;
        }
        .sec-sub {
          font-size: 13px;
          color: var(--tg-mute, #5b6678);
          line-height: 1.5;
          margin: 0;
        }
      `}</style>
    </div>
  )
}

// ───────── 1. Витрина в цифрах ─────────

function StatsSection({ state, channelUUID }: { state: { activatedAt?: string }; channelUUID?: string }) {
  const activated = state.activatedAt ? new Date(state.activatedAt) : null
  const [counts, setCounts] = useState<PostsCountsByStatus | null>(null)
  const [overview, setOverview] = useState<ShowcaseOverview | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!channelUUID) {
      setLoading(false)
      return
    }
    setLoading(true)
    Promise.allSettled([
      getShowcasePosts(channelUUID, { limit: 1 }),
      getShowcaseOverview(channelUUID),
    ])
      .then(([postsRes, overviewRes]) => {
        if (postsRes.status === 'fulfilled') {
          // Тот же snake↔camel баг что в PostsPage (16.06): B6 перешёл на
          // camelCase, но PostStatus enum = snake_case → counts.quality_rejected
          // = undefined → total = NaN. Нормализуем под snake чтобы
          // Record<PostStatus, number> совпадал.
          const rawAny = (postsRes.value.countsByStatus
            ?? (postsRes.value as unknown as { counts_by_status?: Record<string, number> }).counts_by_status
            ?? null) as Record<string, number> | null
          if (rawAny) {
            setCounts({
              queued: rawAny.queued ?? 0,
              processing: rawAny.processing ?? 0,
              published: rawAny.published ?? 0,
              quality_rejected: rawAny.qualityRejected ?? rawAny.quality_rejected ?? 0,
              deindexed: rawAny.deindexed ?? 0,
              failed: rawAny.failed ?? 0,
            })
          }
        }
        if (overviewRes.status === 'fulfilled') {
          setOverview(overviewRes.value)
        }
      })
      .finally(() => setLoading(false))
  }, [channelUUID])

  // Total в Витрине = всё кроме failed (failed — это технические ошибки, юзеру
  // их в «постов опубликовано» считать некорректно).
  const total = counts
    ? counts.queued + counts.processing + counts.published + counts.quality_rejected + counts.deindexed
    : null
  const inSearch = counts?.published ?? null
  const inProgress = counts ? counts.queued + counts.processing : null
  const readers30d = overview?.visits.last30d.visits ?? null

  return (
    <section className="sec">
      <div className="sec-head">
        <h3 className="sec-title">Витрина в цифрах</h3>
      </div>

      <div className="stats-grid">
        <Stat label="Работает с" value={activated ? formatRuDate(activated) : '—'} />
        <Stat
          label="Постов в Витрине"
          value={loading ? '…' : total !== null ? total.toLocaleString('ru-RU') : '—'}
        />
        <Stat
          label="В поиске"
          value={loading ? '…' : inSearch !== null ? inSearch.toLocaleString('ru-RU') : '—'}
          hint={inProgress && inProgress > 0 ? `+${inProgress} в обработке` : undefined}
        />
        <Stat
          label="Читателей за 30 дней"
          value={loading ? '…' : readers30d !== null ? readers30d.toLocaleString('ru-RU') : '—'}
        />
      </div>

      <style>{`
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
        }
        @media (max-width: 700px) {
          .stats-grid { grid-template-columns: repeat(2, 1fr); }
        }
        .stat {
          background: var(--tg-paper, #fafafd);
          border-radius: 10px;
          padding: 12px 14px;
        }
        .stat-label {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--tg-mute, #5b6678);
          margin-bottom: 4px;
        }
        .stat-value {
          font-size: 18px;
          font-weight: 800;
          color: var(--tg-ink, #111);
          font-variant-numeric: tabular-nums;
        }
        .stat-value.todo {
          color: var(--tg-mute, #5b6678);
        }
      `}</style>
    </section>
  )
}

function Stat({ label, value, todo, hint }: { label: string; value: string; todo?: boolean; hint?: string }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className={`stat-value ${todo ? 'todo' : ''}`}>{value}</div>
      {hint && <div className="stat-hint">{hint}</div>}
      <style>{`
        .stat-hint {
          font-size: 11px;
          color: var(--tg-mute, #5b6678);
          margin-top: 2px;
          font-variant-numeric: tabular-nums;
        }
      `}</style>
    </div>
  )
}

// ───────── 2. Правила публикации + bulk-backfill ─────────

function PublishRulesSection({
  state,
  onChange,
  channelUUID,
}: {
  state: { settings: { whatToPublish?: WhatToPublish }; status: ShowcaseStatus }
  onChange: (payload: { whatToPublish?: WhatToPublish; removeShorts?: boolean }) => Promise<unknown>
  channelUUID?: string
}) {
  const current: WhatToPublish = state.settings.whatToPublish ?? 'all'
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pendingChoice, setPendingChoice] = useState<WhatToPublish | null>(null)

  async function applyChange(next: WhatToPublish, removeShorts: boolean) {
    setSubmitting(true)
    setError(null)
    try {
      // PATCH /api/max/showcase/:uuid (B 16.06, commit a655b9d9) —
      // partial update в active/disabled/grace_3d. removeShorts пока сохраняется
      // как флаг, реальный batch-deindex старых коротких ждёт endpoint от C
      // (B уже запросил, см. сообщение 16.06).
      await onChange({ whatToPublish: next, removeShorts })
      track('showcase_settings_changed', {
        whatToPublish: next,
        removeShorts,
        message: `правила публикации · ${next}${removeShorts ? ' · убрать короткие' : ''}`,
      })
      setPendingChoice(null)
    } catch (e) {
      trackError(e, 'showcase-settings-change')
      // 409 STATE_NOT_EDITABLE — editable только в active/disabled/grace_3d
      // (защита B на случай гонок). В норме не должно срабатывать т.к. эта
      // секция показывается только в этих состояниях.
      if (e instanceof ShowcaseApiError && (e.status === 409 || e.code === 'STATE_NOT_EDITABLE')) {
        setError('Сейчас канал не позволяет менять правила публикации. Обновите страницу.')
      } else {
        setError(e instanceof Error ? e.message : 'Не удалось сохранить')
      }
    } finally {
      setSubmitting(false)
    }
  }

  function handleChoice(next: WhatToPublish) {
    if (next === current) return
    // §8: при смене All → Long нужна модалка про судьбу коротких постов
    if (current === 'all' && next === 'long') {
      setPendingChoice('long')
      return
    }
    applyChange(next, false)
  }

  return (
    <section className="sec">
      <div className="sec-head">
        <div>
          <h3 className="sec-title">Какие посты публиковать</h3>
          <p className="sec-sub" style={{ marginTop: 4 }}>
            Можно поменять в&nbsp;любой момент. Изменения применяются к&nbsp;новым постам.
          </p>
        </div>
      </div>

      <div className="rules-options" role="radiogroup">
        <RadioOpt
          name="rule"
          value="all"
          current={current}
          title="Все посты"
          sub="Каждый пост канала автоматически попадает в поиск"
          onChange={handleChoice}
          disabled={submitting}
        />
        <RadioOpt
          name="rule"
          value="long"
          current={current}
          title="Только длинные — больше 500 символов"
          sub="Короткие посты («доброе утро», реакции) пропускаем"
          onChange={handleChoice}
          disabled={submitting}
        />
        <RadioOpt
          name="rule"
          value="manual"
          current={current}
          title="Выбирать вручную"
          sub="Новые посты не попадают в поиск автоматически — выберите нужные в «Постах»"
          onChange={handleChoice}
          disabled={submitting}
        />
      </div>

      {error && <div className="rules-error">{error}</div>}

      <BulkBackfillButton channelUUID={channelUUID} status={state.status} />

      {pendingChoice === 'long' && (
        <ShortPostsModal
          onChoose={(removeShorts) => applyChange('long', removeShorts)}
          onClose={() => setPendingChoice(null)}
          submitting={submitting}
        />
      )}

      <style>{`
        .rules-options { display: flex; flex-direction: column; gap: 8px; }
        .rules-error {
          padding: 10px 14px;
          background: rgba(229, 70, 70, 0.1);
          border: 1px solid rgba(229, 70, 70, 0.3);
          color: #c53030;
          border-radius: 10px;
          font-size: 13px;
        }
      `}</style>
    </section>
  )
}

function RadioOpt({
  name, value, current, title, sub, onChange, disabled,
}: {
  name: string
  value: WhatToPublish
  current: WhatToPublish
  title: string
  sub: string
  onChange: (v: WhatToPublish) => void
  disabled: boolean
}) {
  const isOn = current === value
  return (
    <label className={`opt ${isOn ? 'on' : ''}`}>
      <input
        type="radio"
        name={name}
        checked={isOn}
        onChange={() => onChange(value)}
        disabled={disabled}
      />
      <span className="opt-radio" />
      <div className="opt-body">
        <div className="opt-title">{title}</div>
        <div className="opt-sub">{sub}</div>
      </div>

      <style>{`
        .opt {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 13px 16px;
          background: var(--tg-paper, #fafafd);
          border: 1.5px solid transparent;
          border-radius: 10px;
          cursor: pointer;
        }
        .opt:hover { background: var(--tg-surface-2, #f5f5f9); }
        .opt.on { background: var(--tg-blue-bg, #ebf2ff); border-color: var(--tg-blue, #0066ff); }
        .opt input[type="radio"] { position: absolute; opacity: 0; pointer-events: none; }
        .opt-radio {
          width: 18px;
          height: 18px;
          flex-shrink: 0;
          margin-top: 2px;
          border-radius: 50%;
          border: 1.5px solid var(--tg-line-3, #d0d0d8);
          background: white;
          position: relative;
        }
        .opt.on .opt-radio { border-color: var(--tg-blue, #0066ff); }
        .opt.on .opt-radio::after {
          content: '';
          position: absolute;
          inset: 3px;
          border-radius: 50%;
          background: var(--tg-blue, #0066ff);
        }
        .opt-body { flex: 1; min-width: 0; }
        .opt-title { font-size: 13.5px; font-weight: 700; color: var(--tg-ink, #111); margin-bottom: 2px; }
        .opt-sub { font-size: 12px; color: var(--tg-mute, #5b6678); line-height: 1.5; }
      `}</style>
    </label>
  )
}

function ShortPostsModal({
  onChoose, onClose, submitting,
}: {
  onChoose: (removeShorts: boolean) => void
  onClose: () => void
  submitting: boolean
}) {
  return (
    <div className="veil" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="m-title">Что делать с&nbsp;уже опубликованными короткими постами?</h3>
        <p className="m-sub">
          Вы&nbsp;выбрали «только длинные». В&nbsp;поиске уже есть короткие посты канала — нужно решить, что
          с&nbsp;ними.
        </p>

        <div className="m-options">
          <button
            className="m-opt"
            onClick={() => onChoose(false)}
            disabled={submitting}
          >
            <div className="m-opt-title">Оставить в&nbsp;поиске</div>
            <div className="m-opt-sub">Не&nbsp;трогать уже проиндексированные короткие. Новые короткие посты публиковаться не&nbsp;будут.</div>
          </button>
          <button
            className="m-opt m-opt-danger"
            onClick={() => onChoose(true)}
            disabled={submitting}
          >
            <div className="m-opt-title">Убрать из&nbsp;поиска</div>
            <div className="m-opt-sub">Удалим уже опубликованные короткие посты. Поисковики выкинут их&nbsp;из&nbsp;выдачи за&nbsp;5-14&nbsp;дней.</div>
          </button>
        </div>

        <button className="m-cancel" onClick={onClose} disabled={submitting}>Отменить</button>

        <style>{`
          .veil {
            position: fixed;
            inset: 0;
            background: rgba(38, 40, 45, 0.55);
            backdrop-filter: blur(4px);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: 16px;
          }
          .modal {
            background: white;
            border-radius: 16px;
            padding: 28px;
            max-width: 560px;
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 14px;
            box-shadow: 0 24px 56px -16px rgba(0,0,0,0.3);
          }
          .m-title { font-size: 18px; font-weight: 800; margin: 0; color: var(--tg-ink, #111); }
          .m-sub { font-size: 13.5px; color: var(--tg-mute, #5b6678); margin: 0; line-height: 1.55; }
          .m-options { display: flex; flex-direction: column; gap: 10px; margin-top: 4px; }
          .m-opt {
            text-align: left;
            background: var(--tg-paper, #fafafd);
            border: 1.5px solid var(--tg-line-2, #f0f0f4);
            border-radius: 12px;
            padding: 14px 16px;
            cursor: pointer;
            font-family: inherit;
            transition: border-color 150ms, background 150ms;
          }
          .m-opt:hover:not(:disabled) {
            border-color: var(--tg-blue, #0066ff);
            background: var(--tg-blue-bg, #ebf2ff);
          }
          .m-opt-danger:hover:not(:disabled) {
            border-color: #e54646;
            background: #fff5f5;
          }
          .m-opt-title { font-size: 14px; font-weight: 700; color: var(--tg-ink, #111); margin-bottom: 4px; }
          .m-opt-sub { font-size: 12.5px; color: var(--tg-mute, #5b6678); line-height: 1.5; }
          .m-opt:disabled { opacity: 0.5; cursor: not-allowed; }
          .m-cancel {
            background: none;
            border: 0;
            color: var(--tg-mute, #5b6678);
            font-family: inherit;
            font-size: 13px;
            padding: 8px;
            cursor: pointer;
            margin-top: 4px;
          }
          .m-cancel:hover { color: var(--tg-ink, #111); }
        `}</style>
      </div>
    </div>
  )
}

function BulkBackfillButton({
  channelUUID, status,
}: {
  channelUUID?: string
  status: ShowcaseStatus
}) {
  const [submitting, setSubmitting] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [result, setResult] = useState<{
    hours: number
    sourceTotal: number | null
    received: number
    queued: number
    filteredOut: number
    duplicates: number
    ratePerHour: number
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  // C9 на бэке валидирует state.status, но фронт обязан дизейблить кнопку
  // в неактивных состояниях (см. сообщение B 11.06: 409 SHOWCASE_NOT_ACTIVE).
  const canRun = status === 'active' && !!channelUUID

  async function startBackfill() {
    if (!channelUUID) return
    setSubmitting(true)
    setError(null)
    try {
      const res = await bulkBackfillArchive(channelUUID)
      // C9 sync (контракт 15.06): receivedCount/queuedCount/filteredOutCount/
      // duplicateCount. Поддерживаем snake_case fallback (на случай если B не
      // успел задеплоить mapping) и legacy `postsCount` алиас.
      const raw = res as unknown as Record<string, number | undefined>
      const queued = res.queuedCount ?? raw.queued_count ?? res.postsCount ?? 0
      const received = res.receivedCount ?? raw.received_count ?? queued
      const filteredOut = res.filteredOutCount ?? raw.filtered_out_count ?? 0
      const duplicates = res.duplicateCount ?? raw.duplicate_count ?? 0
      const sourceTotalRaw = res.sourcePostsCount ?? raw.source_posts_count
      const sourceTotal = typeof sourceTotalRaw === 'number' ? sourceTotalRaw : null
      setResult({
        hours: res.estimatedCompletionHours,
        sourceTotal,
        received,
        queued,
        filteredOut,
        duplicates,
        ratePerHour: res.rateLimitPerHour,
      })
      track('showcase_bulk_backfill_requested', {
        sourcePostsCount: sourceTotal,
        receivedCount: received,
        queuedCount: queued,
        filteredOutCount: filteredOut,
        duplicateCount: duplicates,
        estimatedHours: res.estimatedCompletionHours,
        message: `бэкфилл запущен · ${received}→${queued} в очередь · ~${res.estimatedCompletionHours}ч`,
      })
    } catch (e) {
      trackError(e, 'showcase-bulk-backfill')
      if (e instanceof ShowcaseApiError && e.code === 'SHOWCASE_NOT_ACTIVE') {
        setError('Включите Витрину, чтобы запустить публикацию архивов')
      } else {
        setError(e instanceof Error ? e.message : 'Не удалось запустить публикацию архива')
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (result) {
    return (
      <div className="bf-status">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 6L9 17l-5-5" />
        </svg>
        <div className="bf-status-body">
          <div>
            <b>Публикуем архив вашего канала в&nbsp;Витрину.</b>{' '}
            Взяли <b>{result.received.toLocaleString('ru-RU')}</b>&nbsp;{pluralPosts(result.received)}
            {result.sourceTotal !== null && result.sourceTotal > result.received && (
              <> (из <b>{result.sourceTotal.toLocaleString('ru-RU')}</b> в&nbsp;канале)</>
            )}
            . Подготовим к&nbsp;поиску{' '}
            <b>{result.queued.toLocaleString('ru-RU')}</b>&nbsp;{pluralPosts(result.queued)}
            {result.filteredOut > 0 && (
              <>, <b>{result.filteredOut.toLocaleString('ru-RU')}</b> пропустим (короткие или&nbsp;без смысла для поисковиков)</>
            )}
            {result.duplicates > 0 && (
              <>, <b>{result.duplicates.toLocaleString('ru-RU')}</b> уже&nbsp;есть в&nbsp;Витрине</>
            )}
            . Займёт примерно <b>{result.hours}&nbsp;{pluralHours(result.hours)}</b> — обрабатываем по&nbsp;{result.ratePerHour} постов/час, чтобы не&nbsp;спугнуть поисковики.
          </div>
          <div className="bf-progress-hint">
            Следите на&nbsp;вкладке <b>«Посты»</b> — счётчик «Готовится» начнёт расти через 5-10&nbsp;минут.
          </div>
        </div>

        <style>{`
          .bf-status {
            display: flex;
            align-items: flex-start;
            gap: 10px;
            padding: 12px 16px;
            background: rgba(0, 168, 96, 0.08);
            border: 1px solid rgba(0, 168, 96, 0.2);
            border-radius: 10px;
            font-size: 13px;
            line-height: 1.5;
            color: var(--tg-ink-2, #333);
          }
          .bf-status svg { width: 18px; height: 18px; color: var(--tg-up, #00a860); flex-shrink: 0; margin-top: 1px; }
          .bf-status-body { display: flex; flex-direction: column; gap: 6px; }
          .bf-funnel {
            font-size: 12px;
            color: var(--tg-mute, #5b6678);
          }
          .bf-progress-hint {
            font-size: 12.5px;
            color: var(--tg-ink-2, #333);
          }
        `}</style>
      </div>
    )
  }

  if (confirming) {
    return (
      <div className="bf-confirm">
        <div className="bf-c-text">
          Опубликовать <b>все</b> архивные посты канала? Мы обработаем постепенно, чтобы не&nbsp;перегружать
          поисковики. Это&nbsp;может&nbsp;занять <b>от&nbsp;нескольких часов до&nbsp;нескольких суток</b>.
        </div>
        {error && <div className="bf-c-error">{error}</div>}
        <div className="bf-c-actions">
          <button className="bf-c-cancel" onClick={() => setConfirming(false)} disabled={submitting}>Отменить</button>
          <button className="bf-c-go" onClick={startBackfill} disabled={submitting}>
            {submitting ? 'Запускаем…' : 'Да, опубликовать всё'}
          </button>
        </div>

        <style>{`
          .bf-confirm {
            background: var(--tg-paper, #fafafd);
            border: 1px dashed var(--tg-line-3, #d0d0d8);
            border-radius: 10px;
            padding: 14px 16px;
            display: flex;
            flex-direction: column;
            gap: 10px;
          }
          .bf-c-text { font-size: 13px; color: var(--tg-ink-2, #333); line-height: 1.5; }
          .bf-c-error {
            padding: 8px 12px;
            background: rgba(229, 70, 70, 0.1);
            border: 1px solid rgba(229, 70, 70, 0.3);
            color: #c53030;
            border-radius: 8px;
            font-size: 12.5px;
          }
          .bf-c-actions { display: flex; gap: 8px; justify-content: flex-end; }
          .bf-c-cancel {
            background: none;
            border: 1px solid var(--tg-line-2, #f0f0f4);
            color: var(--tg-mute, #5b6678);
            font-family: inherit;
            font-size: 13px;
            padding: 8px 14px;
            border-radius: 8px;
            cursor: pointer;
          }
          .bf-c-cancel:hover:not(:disabled) { background: var(--tg-surface, #fff); color: var(--tg-ink, #111); }
          .bf-c-go {
            background: var(--tg-blue, #0066ff);
            color: white;
            font-family: inherit;
            font-size: 13px;
            font-weight: 700;
            padding: 8px 14px;
            border: 0;
            border-radius: 8px;
            cursor: pointer;
          }
          .bf-c-go:hover:not(:disabled) { background: var(--tg-blue-press, #0052cc); }
          .bf-c-go:disabled, .bf-c-cancel:disabled { opacity: 0.5; cursor: not-allowed; }
        `}</style>
      </div>
    )
  }

  return (
    <div className="bf-row">
      <button
        className="bf-trigger"
        onClick={() => setConfirming(true)}
        disabled={!canRun}
        title={!canRun ? 'Включите Витрину, чтобы запустить публикацию архивов' : undefined}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 12a9 9 0 109-9c2.5 0 4.8 1 6.5 2.7L21 8M21 3v5h-5" />
        </svg>
        Опубликовать все архивные посты
      </button>
      {!canRun && status !== 'active' && (
        <div className="bf-hint">Доступно только при активной Витрине</div>
      )}

      <style>{`
        .bf-row { display: flex; flex-direction: column; gap: 6px; align-items: flex-start; }
        .bf-trigger {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          background: var(--tg-surface, #fff);
          color: var(--tg-ink-2, #333);
          border: 1.5px dashed var(--tg-line-3, #d0d0d8);
          border-radius: 10px;
          font-family: inherit;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
        }
        .bf-trigger:hover:not(:disabled) { border-color: var(--tg-blue, #0066ff); color: var(--tg-blue-press, #0052cc); }
        .bf-trigger:disabled { opacity: 0.55; cursor: not-allowed; }
        .bf-trigger svg { width: 14px; height: 14px; }
        .bf-hint {
          font-size: 11.5px;
          color: var(--tg-mute, #5b6678);
        }
      `}</style>
    </div>
  )
}

function pluralPosts(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'пост'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'поста'
  return 'постов'
}

// ───────── 3. Согласие ─────────

function ConsentSection({ state }: { state: { consent?: { acceptedAt: string; consentVersion: string; userId: string } } }) {
  const consent = state.consent
  return (
    <section className="sec">
      <div className="sec-head">
        <h3 className="sec-title">Согласие на публикацию</h3>
      </div>

      {consent ? (
        <div className="consent-info">
          <ConsentRow label="Принято" value={formatRuDateTime(new Date(consent.acceptedAt))} />
          <ConsentRow label="Версия условий" value={consent.consentVersion} mono />
          <ConsentRow label="Аккаунт" value={consent.userId} mono />
        </div>
      ) : (
        <div className="consent-empty">Информация о согласии недоступна</div>
      )}

      <div className="consent-actions">
        <a className="consent-link" href="/legal/showcase" target="_blank" rel="noopener noreferrer">
          Условия публикации
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 17L17 7M7 7h10v10" />
          </svg>
        </a>
      </div>

      <style>{`
        .consent-info {
          display: flex;
          flex-direction: column;
          gap: 6px;
          background: var(--tg-paper, #fafafd);
          border-radius: 10px;
          padding: 12px 16px;
        }
        .consent-empty {
          padding: 12px 16px;
          background: var(--tg-paper, #fafafd);
          border-radius: 10px;
          font-size: 13px;
          color: var(--tg-mute, #5b6678);
        }
        .c-row {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          gap: 12px;
          font-size: 13px;
        }
        .c-row-label { color: var(--tg-mute, #5b6678); }
        .c-row-val { color: var(--tg-ink, #111); font-weight: 600; text-align: right; word-break: break-all; }
        .c-row-val.mono { font-family: ui-monospace, SFMono-Regular, monospace; font-size: 12px; }
        .consent-actions { display: flex; gap: 10px; }
        .consent-link {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 13px;
          color: var(--tg-blue-press, #0052cc);
          text-decoration: none;
          font-weight: 600;
        }
        .consent-link:hover { text-decoration: underline; }
        .consent-link svg { width: 12px; height: 12px; }
      `}</style>
    </section>
  )
}

function ConsentRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="c-row">
      <div className="c-row-label">{label}</div>
      <div className={`c-row-val ${mono ? 'mono' : ''}`}>{value}</div>
    </div>
  )
}

// ───────── 4. Опасная зона ─────────

function DangerZone({ state, onDisable }: { state: { status: string }; onDisable: () => Promise<unknown> }) {
  const [confirming, setConfirming] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isActive = state.status === 'active' || state.status === 'grace_3d'

  async function doDisable() {
    setSubmitting(true)
    setError(null)
    try {
      await onDisable()
      track('showcase_disabled', { message: 'Витрина выключена юзером' })
      setConfirming(false)
    } catch (e) {
      trackError(e, 'showcase-disable')
      setError(e instanceof Error ? e.message : 'Не удалось выключить')
    } finally {
      setSubmitting(false)
    }
  }

  if (!isActive) {
    return (
      <section className="sec">
        <div className="sec-head">
          <div>
            <h3 className="sec-title">Витрина выключена</h3>
            <p className="sec-sub" style={{ marginTop: 4 }}>
              Включить можно на&nbsp;вкладке «Обзор» или с&nbsp;помощью кнопки ниже.
            </p>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="sec sec-danger">
      <div className="sec-head">
        <div>
          <h3 className="sec-title">Опасная зона</h3>
          <p className="sec-sub" style={{ marginTop: 4 }}>
            Выключение Витрины. Понадобится время на&nbsp;восстановление видимости в&nbsp;поиске.
          </p>
        </div>
      </div>

      {!confirming ? (
        <button className="dz-trigger" onClick={() => setConfirming(true)}>
          Выключить Витрину
        </button>
      ) : (
        <div className="dz-confirm">
          <div className="dz-warn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
            <div>
              <b>Перед выключением убедитесь:</b>
              <ul>
                <li>Удаление из&nbsp;Яндекса и&nbsp;Google занимает <b>5-14 дней</b></li>
                <li>Обратное включение мгновенно, но&nbsp;возврат в&nbsp;выдачу — нет (тоже 2-4 недели)</li>
                <li>Бесплатный трафик из&nbsp;поиска прекратится</li>
              </ul>
            </div>
          </div>

          {error && <div className="dz-error">{error}</div>}

          <div className="dz-actions">
            <button className="dz-cancel" onClick={() => setConfirming(false)} disabled={submitting}>Отменить</button>
            <button className="dz-go" onClick={doDisable} disabled={submitting}>
              {submitting ? 'Выключаем…' : 'Да, выключить'}
            </button>
          </div>
        </div>
      )}

      <style>{`
        .dz-trigger {
          align-self: flex-start;
          background: white;
          color: #c53030;
          border: 1.5px solid rgba(229, 70, 70, 0.4);
          border-radius: 10px;
          padding: 10px 16px;
          font-family: inherit;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
        }
        .dz-trigger:hover { background: #fff5f5; border-color: #e54646; }
        .dz-confirm { display: flex; flex-direction: column; gap: 12px; }
        .dz-warn {
          display: flex;
          gap: 12px;
          padding: 14px 16px;
          background: white;
          border: 1px solid rgba(229, 70, 70, 0.3);
          border-radius: 10px;
          color: var(--tg-ink-2, #333);
          font-size: 13px;
          line-height: 1.55;
        }
        .dz-warn svg { width: 18px; height: 18px; color: #c53030; flex-shrink: 0; margin-top: 2px; }
        .dz-warn ul { margin: 4px 0 0; padding-left: 18px; }
        .dz-warn li { margin: 4px 0; }
        .dz-warn b { color: var(--tg-ink, #111); }
        .dz-error {
          padding: 10px 14px;
          background: rgba(229, 70, 70, 0.1);
          border: 1px solid rgba(229, 70, 70, 0.3);
          color: #c53030;
          border-radius: 10px;
          font-size: 13px;
        }
        .dz-actions { display: flex; gap: 8px; justify-content: flex-end; }
        .dz-cancel {
          background: white;
          border: 1px solid var(--tg-line-2, #f0f0f4);
          color: var(--tg-mute, #5b6678);
          font-family: inherit;
          font-size: 13px;
          padding: 8px 14px;
          border-radius: 8px;
          cursor: pointer;
        }
        .dz-cancel:hover:not(:disabled) { color: var(--tg-ink, #111); }
        .dz-go {
          background: #c53030;
          color: white;
          font-family: inherit;
          font-size: 13px;
          font-weight: 700;
          padding: 8px 14px;
          border: 0;
          border-radius: 8px;
          cursor: pointer;
        }
        .dz-go:hover:not(:disabled) { background: #a02020; }
        .dz-go:disabled, .dz-cancel:disabled { opacity: 0.5; cursor: not-allowed; }
      `}</style>
    </section>
  )
}

// ───────── helpers ─────────

function pluralDuplicates(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'дубликат'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'дубликата'
  return 'дубликатов'
}

function pluralHours(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'час'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'часа'
  return 'часов'
}

const MONTHS_RU = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']

function formatRuDate(d: Date): string {
  return `${d.getDate()} ${MONTHS_RU[d.getMonth()]} ${d.getFullYear()}`
}

function formatRuDateTime(d: Date): string {
  const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  return `${formatRuDate(d)}, ${time}`
}
